/**
 * File upload utilities for document validation and processing
 */

// Import and re-export shared types
import type { FileValidationResult } from '../types/fileUpload';
export type { UploadedFile, FileValidationResult } from '../types/fileUpload';

// Supported file formats
export const SUPPORTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/tiff': ['.tif', '.tiff'],
} as const;

export const SUPPORTED_EXTENSIONS = Object.values(SUPPORTED_FILE_TYPES).flat();

// File size limits
export const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB in bytes

/**
 * Validate file type and size
 */
export const validateFile = (file: File): FileValidationResult => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size exceeds ${formatFileSize(MAX_FILE_SIZE)} limit`
    };
  }

  // Check file type
  const isValidType = Object.keys(SUPPORTED_FILE_TYPES).includes(file.type) || 
    SUPPORTED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));

  if (!isValidType) {
    return {
      isValid: false,
      error: 'Only PDF, DOCX, JPEG, JPG, PNG, TIF and TIFF files are supported'
    };
  }

  return { isValid: true };
};

/**
 * Format file size in human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2);
};

/**
 * Generate unique file ID
 */
export const generateFileId = (): string => {
  // Use crypto for stronger uniqueness (Sonar: avoid Math.random for IDs)
  try {
    const arr = new Uint8Array(8); // 8 bytes => 64 bits entropy
  // Fallback to window.crypto only if present (typed as unknown first to avoid any)
    const cryptoObj: unknown = (globalThis as unknown as { crypto?: Crypto }).crypto ?? (typeof window !== 'undefined' ? (window as unknown as { crypto?: Crypto }).crypto : undefined);
    if (cryptoObj && typeof (cryptoObj as Crypto).getRandomValues === 'function') {
      (cryptoObj as Crypto).getRandomValues(arr);
    } else {
      throw new Error('crypto unavailable');
    }
    // Convert to base36 string
    let num = 0n;
    for (const b of arr) { num = (num << 8n) | BigInt(b); }
    let base36 = num.toString(36);
    // Ensure deterministic length: pad if shorter then slice to 13
    if (base36.length < 13) {
      base36 = base36.padStart(13, '0');
    }
    return base36.slice(0, 13); // fixed 13-char id
  } catch {
    // Fallback: timestamp + random segment (non-crypto) with clear comment
    return 'f_' + Date.now().toString(36);
  }
};
